import type { SagaIterator } from 'redux-saga';
import { takeEvery, select, put, take } from 'redux-saga/effects';
import { toUtcTime } from '@core/time/time';
import { track } from '@engage/analytics/actions';
import { trackViewedClassDetails } from '@engage/class-detail/analytics';
import type { openClassDetailAnalytic } from '@engage/class-detail/redux';
import { ClassDetailActionType } from '@engage/class-detail/redux';
import { getClassDetail } from '@engage/class-detail/selectors';
import type { ClassAnalyticsProperties } from '@engage/classes/analytics';
import { getClassAnalyticsProperties } from '@engage/classes/selectors';
import { logError } from '@engage/error-reporting/logError';
import { getFilterAnalyticsProps } from '@engage/filters/selectors';
import type { Member } from '@engage/members/models/Member';
import { getMember } from '@engage/members/selectors';
import { toSource } from '@members/analytics/api';
import {
  PROGRAMS_LOCATION_SOURCE,
  toProgramGroupProperties,
} from '@members/class-groups/analytics/analytics';
import { FEED_CLASS_DETAILS_SOURCE } from '@members/pg-feed/constants';
import { getProgramInfo } from '@members/pg-programs/redux';
import { PROGRAMS_ID_NEXTJS_PARAM, PROGRAMS_ROUTE } from '@members/routes/programs';

const getClassGatedType = (
  member: Member,
  contentAvailability: string,
  freeForLimitedTime: boolean,
) => {
  if (!member?.hasActiveDeviceSubscription && !member?.hasActiveDigitalSubscription) {
    if (contentAvailability === 'available' && freeForLimitedTime) {
      return 'Free Class - Limited time';
    } else if (contentAvailability === 'available') {
      return 'Free Class';
    }
  }

  return 'none';
};

const analyticsSaga = function* (
  action: ReturnType<typeof openClassDetailAnalytic>,
): SagaIterator {
  try {
    // Wait for class to load before sending track action
    yield take(ClassDetailActionType.RequestSuccess);
    const classInfo: ClassAnalyticsProperties | undefined = yield select(
      getClassAnalyticsProperties,
      action.payload.id,
    );
    const classDetails = yield select(getClassDetail);
    const member = yield select(getMember);
    const classGatedType = getClassGatedType(
      member,
      classDetails.ride.contentAvailability,
      classDetails.ride.freeForLimitedTime,
    );
    const browseInfo = yield select(getFilterAnalyticsProps);
    const hasPlaylist = classDetails.playlist.isPlaylistShown;

    const isViewedFromProgramDetails =
      action.payload.router.pathname === `${PROGRAMS_ROUTE}${PROGRAMS_ID_NEXTJS_PARAM}`;

    if (isViewedFromProgramDetails) {
      const programInfo = yield select(getProgramInfo);
      const dateFormat = 'YYYY-MM-DD';
      const programAnalytics = {
        ...toProgramGroupProperties(programInfo.programClassGroup),
        '[Program Progress ID]': programInfo.programProgressId,
        programProgressStartDate: toUtcTime(programInfo.programProgressStartDate).format(
          dateFormat,
        ),
        programProgressEndDate: toUtcTime(programInfo.programProgressEndDate).format(
          dateFormat,
        ),
      };

      yield put(
        track(
          trackViewedClassDetails(
            classInfo,
            PROGRAMS_LOCATION_SOURCE,
            false,
            hasPlaylist,
            browseInfo,
            action.payload.isInStacks,
            programAnalytics,
            action.payload.searchSharedProperties,
            {},
            classGatedType,
          ),
        ),
      );
    } else if (action.payload.router.pathname.includes('home')) {
      const featuredAnalytics = {
        ['Featured Name']: action.payload.router.query.featuredName ?? undefined,
        ['Rank']: action.payload.router.query.rank ?? undefined,
        ['Featured Total Classes']: action.payload.router.query.total ?? undefined,
      };
      yield put(
        track(
          trackViewedClassDetails(
            classInfo,
            toSource(action.payload.router.query) as string,
            false,
            hasPlaylist,
            browseInfo,
            action.payload.isInStacks,
            undefined,
            action.payload.searchSharedProperties,
            featuredAnalytics,
            classGatedType,
          ),
        ),
      );
    } else if (action.payload.router.pathname.includes('feed')) {
      yield put(
        track(
          trackViewedClassDetails(
            classInfo,
            FEED_CLASS_DETAILS_SOURCE,
            false,
            hasPlaylist,
            browseInfo,
            action.payload.isInStacks,
            undefined,
            action.payload.searchSharedProperties,
            {},
            classGatedType,
          ),
        ),
      );
    } else {
      yield put(
        track(
          trackViewedClassDetails(
            classInfo,
            toSource(action.payload.router.query) as string,
            false,
            hasPlaylist,
            browseInfo,
            action.payload.isInStacks,
            undefined,
            action.payload.searchSharedProperties,
            {},
            classGatedType,
          ),
        ),
      );
    }
  } catch (e) {
    logError(e, 'analyticsSaga');
  }
};

export default function* () {
  yield takeEvery(ClassDetailActionType.OpenAnalytic, analyticsSaga);
}
